/**
 * User Role Configuration
 * Defines the three primary user role types and their associated permissions
 *
 * Role Types:
 * 1. Super User - Full access to everything, can be assigned to appointments
 * 2. Scheduling Assistant - Office staff, schedules for others, cannot be assigned to appointments
 * 3. Team Member - Field staff, view own bookings, default role for new users
 *
 * These are protected system roles that can only be modified by the system admin.
 */

/**
 * Core role type identifiers
 */
export type UserRoleType = 'booking_admin' | 'user' | 'super_user';

/**
 * System role names that match the backend SystemRoleNames constants.
 * These roles are protected and cannot be deleted or renamed.
 */
export const SYSTEM_ROLE_NAMES = ['Super User', 'Scheduling Assistant', 'Team Member'] as const;
export type SystemRoleName = typeof SYSTEM_ROLE_NAMES[number];

/**
 * Role configuration interface
 */
export interface UserRoleDefinition {
  id: UserRoleType;
  name: string;
  displayName: string;
  description: string;
  
  // Core capabilities
  canManageAllBookings: boolean;        // Manage bookings for all users (not just own)
  canManageRequests: boolean;            // View and manage booking requests
  canReassignUsers: boolean;             // Reassign users to bookings/appointments
  canReschedule: boolean;                // Reschedule appointments
  canCancel: boolean;                    // Cancel bookings/appointments
  canChangeStatuses: boolean;            // Change booking/request statuses
  
  // Booking form capabilities
  canEditBookingForms: boolean;          // Edit booking form settings
  canCreateBookingForms: boolean;        // Create new booking forms
  
  // Assignment capabilities
  canBeAssignedToEvents: boolean;        // Can be assigned to appointments/events
  isAssignableByDefault: boolean;        // Default value for assignability toggle
  
  // Company settings
  canEditCompanySettings: boolean;       // Edit company-level settings
  hasCompanySettingsToggle: boolean;     // Whether role has toggle for company settings
  
  // Dashboard/View permissions
  canViewAllUsersDashboard: boolean;     // View dashboard for all users
  canViewOwnDashboardOnly: boolean;      // Limited to own dashboard view
  
  // Permissions list (for ABP integration)
  defaultPermissions: string[];
}

/**
 * Role definitions for the three user types
 */
export const USER_ROLE_DEFINITIONS: Record<UserRoleType, UserRoleDefinition> = {
  /**
   * Scheduling Assistant
   * - Schedules and assigns appointments for team members
   * - Can manage bookings, requests, re-assign users, re-schedule, cancel, change statuses for all users
   * - Can edit and create booking forms
   * - CANNOT be assigned to an event themselves (they schedule for others)
   * - Has a toggle to enable/disable company settings access
   */
  booking_admin: {
    id: 'booking_admin',
    name: 'Scheduling Assistant', // Matches backend SystemRoleNames.SchedulingAssistant
    displayName: 'Scheduling Assistant',
    description: 'Schedules and assigns appointments for team members. Cannot be assigned to appointments themselves. Optional company settings access.',
    
    canManageAllBookings: true,
    canManageRequests: true,
    canReassignUsers: true,
    canReschedule: true,
    canCancel: true,
    canChangeStatuses: true,
    
    canEditBookingForms: true,
    canCreateBookingForms: true,
    
    canBeAssignedToEvents: false,
    isAssignableByDefault: false,
    
    canEditCompanySettings: false, // Controlled by toggle
    hasCompanySettingsToggle: true,
    
    canViewAllUsersDashboard: true,
    canViewOwnDashboardOnly: false,
    
    defaultPermissions: [
      'Routemize.Booking.FullAccess',
      'Routemize.Booking.View',
      'Routemize.Booking.Create',
      'Routemize.Booking.Update',
      'Routemize.Booking.Delete',
      'Routemize.Requests.FullAccess',
      'Routemize.Requests.View',
      'Routemize.Requests.Update',
      'Routemize.Requests.Reassign',
      'Routemize.Appointments.Reschedule',
      'Routemize.Appointments.Cancel',
      'Routemize.Appointments.ChangeStatus',
      'Routemize.BookingForms.FullAccess',
      'Routemize.BookingForms.View',
      'Routemize.BookingForms.Create',
      'Routemize.BookingForms.Update',
      'Routemize.BookingForms.Delete',
      'Routemize.Dashboard.ViewAll',
      'Routemize.Team.View'
    ]
  },
  
  /**
   * User (Field Team Member)
   * - Field team members who can be assigned to appointments
   * - Can only see their own bookings and dashboard
   * - Cannot edit booking form settings or company settings
   * - Can change status (Scheduled → On the Way → In Progress → Complete /
   *   No Show / Cancelled) on their **own** assigned appointments. Cannot
   *   reschedule, edit address, or modify other appointment fields.
   *   See `docs/implementation/team-member-change-status-backend-spec.md`.
   */
  user: {
    id: 'user',
    name: 'Team Member', // Matches backend SystemRoleNames.TeamMember
    displayName: 'Team Member',
    description: 'Field team member who can be assigned to appointments. Access limited to own bookings and dashboard. Can change status on own appointments.',
    
    canManageAllBookings: false,
    canManageRequests: false,
    canReassignUsers: false,
    canReschedule: false,
    canCancel: false,
    canChangeStatuses: true,
    
    canEditBookingForms: false,
    canCreateBookingForms: false,
    
    canBeAssignedToEvents: true, // Controlled per-user via isAssignable flag
    isAssignableByDefault: true,
    
    canEditCompanySettings: false,
    hasCompanySettingsToggle: false,
    
    canViewAllUsersDashboard: false,
    canViewOwnDashboardOnly: true,
    
    defaultPermissions: [
      'Routemize.Booking.ViewOwn',
      'Routemize.Dashboard.ViewOwn',
      'Routemize.Profile.Update',
      'Routemize.Appointments.ViewAssigned',
      // NOTE: The backend permission key uses the singular `Appointment.X`
      // namespace (matching `Routemize.Appointment.Update`), not the plural
      // `Appointments.X` form used by the older aspirational constants in
      // this file. `PermissionManager.canChangeStatuses` checks the
      // singular form against the ABP grantedPolicies, so this is the
      // authoritative key the backend must grant to the Team Member role.
      'Routemize.Appointment.ChangeStatus',
      // Grants Team Members the ability to plan / add / bulk-route ad-hoc
      // stops onto their *own* schedule via
      // `POST /api/app/appointment/createStop`. The backend authorizes by
      // requiring this permission on the endpoint AND enforcing that
      // `assignedUserId` equals the caller's user id (Team Members cannot
      // schedule stops onto someone else's day). See
      // `docs/implementation/team-member-create-own-stop-backend-spec.md`.
      'Routemize.Appointment.CreateStop',
      // Grants Team Members the ability to hard-delete an ad-hoc stop
      // they created on their *own* schedule via
      // `DELETE /api/app/appointment/{id}/delete`. The backend authorizes
      // by requiring this permission AND enforcing that the row has
      // `Source = InternalStop` and `AssignedUserId == currentUser.id`
      // (Team Members cannot delete real customer appointments or
      // someone else's ad-hoc stops). See
      // `docs/implementation/team-member-delete-own-ad-hoc-stop-backend-spec.md`.
      'Routemize.Appointment.DeleteOwnStop'
    ]
  },
  
  /**
   * Super User
   * - Can manage bookings, requests, re-assign users, re-schedule, cancel, change statuses for all users
   * - CAN be assigned to appointments (unlike Scheduling Assistant)
   * - Can edit and create booking forms
   * - Can edit company settings
   */
  super_user: {
    id: 'super_user',
    name: 'Super User', // Matches backend SystemRoleNames.SuperUser
    displayName: 'Super User',
    description: 'Full access to all features including being assignable to appointments and company settings.',
    
    canManageAllBookings: true,
    canManageRequests: true,
    canReassignUsers: true,
    canReschedule: true,
    canCancel: true,
    canChangeStatuses: true,
    
    canEditBookingForms: true,
    canCreateBookingForms: true,
    
    canBeAssignedToEvents: true,
    isAssignableByDefault: true,
    
    canEditCompanySettings: true,
    hasCompanySettingsToggle: false, // Always has access
    
    canViewAllUsersDashboard: true,
    canViewOwnDashboardOnly: false,
    
    defaultPermissions: [
      'Routemize.Booking.FullAccess',
      'Routemize.Booking.View',
      'Routemize.Booking.Create',
      'Routemize.Booking.Update',
      'Routemize.Booking.Delete',
      'Routemize.Requests.FullAccess',
      'Routemize.Requests.View',
      'Routemize.Requests.Update',
      'Routemize.Requests.Reassign',
      'Routemize.Appointments.Reschedule',
      'Routemize.Appointments.Cancel',
      'Routemize.Appointments.ChangeStatus',
      'Routemize.Appointments.ViewAssigned',
      'Routemize.Appointments.Assignable',
      'Routemize.BookingForms.FullAccess',
      'Routemize.BookingForms.View',
      'Routemize.BookingForms.Create',
      'Routemize.BookingForms.Update',
      'Routemize.BookingForms.Delete',
      'Routemize.Dashboard.ViewAll',
      'Routemize.Team.View',
      'Routemize.Settings.FullAccess',
      'Routemize.Settings.Company'
    ]
  }
};

/**
 * Extended user properties for role-specific settings
 */
export interface UserRoleSettings {
  roleType: UserRoleType;
  isAssignableToEvents: boolean;      // Whether this specific user can be assigned to events
  canEditCompanySettings: boolean;    // For Scheduling Assistant role toggle
}

/**
 * Permission names for the booking system
 */
export const BOOKING_PERMISSIONS = {
  // Booking permissions
  BOOKING_FULL_ACCESS: 'Routemize.Booking.FullAccess',
  BOOKING_VIEW: 'Routemize.Booking.View',
  BOOKING_VIEW_OWN: 'Routemize.Booking.ViewOwn',
  BOOKING_CREATE: 'Routemize.Booking.Create',
  BOOKING_UPDATE: 'Routemize.Booking.Update',
  BOOKING_DELETE: 'Routemize.Booking.Delete',
  
  // Request permissions
  REQUESTS_FULL_ACCESS: 'Routemize.Requests.FullAccess',
  REQUESTS_VIEW: 'Routemize.Requests.View',
  REQUESTS_UPDATE: 'Routemize.Requests.Update',
  REQUESTS_REASSIGN: 'Routemize.Requests.Reassign',
  
  // Appointment permissions
  APPOINTMENTS_RESCHEDULE: 'Routemize.Appointments.Reschedule',
  APPOINTMENTS_CANCEL: 'Routemize.Appointments.Cancel',
  APPOINTMENTS_CHANGE_STATUS: 'Routemize.Appointments.ChangeStatus',
  APPOINTMENTS_VIEW_ASSIGNED: 'Routemize.Appointments.ViewAssigned',
  APPOINTMENTS_ASSIGNABLE: 'Routemize.Appointments.Assignable',
  
  // Booking form permissions
  BOOKING_FORMS_FULL_ACCESS: 'Routemize.BookingForms.FullAccess',
  BOOKING_FORMS_VIEW: 'Routemize.BookingForms.View',
  BOOKING_FORMS_CREATE: 'Routemize.BookingForms.Create',
  BOOKING_FORMS_UPDATE: 'Routemize.BookingForms.Update',
  BOOKING_FORMS_DELETE: 'Routemize.BookingForms.Delete',
  
  // Dashboard permissions
  DASHBOARD_VIEW_ALL: 'Routemize.Dashboard.ViewAll',
  DASHBOARD_VIEW_OWN: 'Routemize.Dashboard.ViewOwn',
  
  // Settings permissions
  SETTINGS_FULL_ACCESS: 'Routemize.Settings.FullAccess',
  SETTINGS_COMPANY: 'Routemize.Settings.Company',
  
  // Team permissions
  TEAM_VIEW: 'Routemize.Team.View',
  
  // Profile permissions
  PROFILE_UPDATE: 'Routemize.Profile.Update'
} as const;

export type BookingPermission = typeof BOOKING_PERMISSIONS[keyof typeof BOOKING_PERMISSIONS];

/**
 * Helper functions for role management
 */
export class UserRoleConfigManager {
  /**
   * Get role definition by role type
   */
  static getRoleDefinition(roleType: UserRoleType): UserRoleDefinition {
    return USER_ROLE_DEFINITIONS[roleType];
  }
  
  /**
   * Get role definition by role name (case-insensitive)
   */
  static getRoleDefinitionByName(roleName: string): UserRoleDefinition | null {
    const normalizedName = roleName.toLowerCase().replace(/[_\s]/g, '');
    
    for (const role of Object.values(USER_ROLE_DEFINITIONS)) {
      const normalizedRoleName = role.name.toLowerCase().replace(/[_\s]/g, '');
      if (normalizedRoleName === normalizedName) {
        return role;
      }
    }
    
    // Try matching display name
    for (const role of Object.values(USER_ROLE_DEFINITIONS)) {
      const normalizedDisplayName = role.displayName.toLowerCase().replace(/[_\s]/g, '');
      if (normalizedDisplayName === normalizedName) {
        return role;
      }
    }
    
    return null;
  }
  
  /**
   * Get all role definitions
   */
  static getAllRoleDefinitions(): UserRoleDefinition[] {
    return Object.values(USER_ROLE_DEFINITIONS);
  }
  
  /**
   * Check if a role can manage all bookings (not just own)
   */
  static canManageAllBookings(roleType: UserRoleType): boolean {
    return USER_ROLE_DEFINITIONS[roleType].canManageAllBookings;
  }
  
  /**
   * Check if a role can be assigned to events
   */
  static canBeAssignedToEvents(roleType: UserRoleType): boolean {
    return USER_ROLE_DEFINITIONS[roleType].canBeAssignedToEvents;
  }
  
  /**
   * Check if a role can edit booking forms
   */
  static canEditBookingForms(roleType: UserRoleType): boolean {
    return USER_ROLE_DEFINITIONS[roleType].canEditBookingForms;
  }
  
  /**
   * Check if a role can edit company settings
   * Note: For Scheduling Assistant (booking_admin), this depends on the user-level toggle
   */
  static canEditCompanySettings(roleType: UserRoleType, userOverride?: boolean): boolean {
    const role = USER_ROLE_DEFINITIONS[roleType];
    
    // If role has a toggle and user override is provided, use the override
    if (role.hasCompanySettingsToggle && userOverride !== undefined) {
      return userOverride;
    }
    
    return role.canEditCompanySettings;
  }
  
  /**
   * Get default permissions for a role
   */
  static getDefaultPermissions(roleType: UserRoleType): string[] {
    return [...USER_ROLE_DEFINITIONS[roleType].defaultPermissions];
  }
  
  /**
   * Get permissions for Scheduling Assistant (booking_admin) with company settings toggle
   */
  static getSchedulingAssistantPermissions(canEditCompanySettings: boolean): string[] {
    const basePermissions = [...USER_ROLE_DEFINITIONS.booking_admin.defaultPermissions];
    
    if (canEditCompanySettings) {
      basePermissions.push(
        BOOKING_PERMISSIONS.SETTINGS_FULL_ACCESS,
        BOOKING_PERMISSIONS.SETTINGS_COMPANY
      );
    }
    
    return basePermissions;
  }
  
  /**
   * Determine role type from a list of role names
   * Handles both new system role names and legacy names for backwards compatibility
   */
  static determineRoleType(roleNames: string[]): UserRoleType {
    const normalizedNames = roleNames.map(name => name.toLowerCase().replace(/[_\s]/g, ''));

    // Check for Super User first (highest privilege)
    // Matches: "Super User", "SuperUser", "super_user", legacy "admin"
    if (normalizedNames.some(name =>
      name === 'superuser' ||
      name === 'super_user' ||
      name === 'admin' || // Legacy admin role maps to Super User
      name === 'hostadmin' || // Host Admin (host staff; the host 'admin' role's successor)
      name === 'hostadminlimited' || // Host Admin Limited
      name.includes('super')
    )) {
      return 'super_user';
    }

    const isSchedulingAssistant = normalizedNames.some(name =>
      name === 'schedulingassistant' ||
      name === 'scheduling_assistant' ||
      name === 'bookingadmin' ||
      name === 'booking_admin' ||
      (name.includes('scheduling') && name.includes('assistant')) ||
      (name.includes('booking') && name.includes('admin'))
    );

    const isTeamMember = normalizedNames.some(name =>
      name === 'teammember' ||
      name === 'team_member' ||
      name === 'user' ||
      (name.includes('team') && name.includes('member'))
    );

    // Users with both Team Member and Scheduling Assistant remain field-assignable;
    // SA-only users schedule for others and cannot receive appointments.
    if (isSchedulingAssistant && isTeamMember) {
      return 'user';
    }

    if (isSchedulingAssistant) {
      return 'booking_admin';
    }

    // Default to Team Member (user role)
    return 'user';
  }

  /**
   * Whether the role names include the Scheduling Assistant role.
   * Unlike determineRoleType (which resolves SA + Team Member combos to
   * 'user' for assignability purposes), this reports SA membership directly
   * so office-staff features stay visible to users holding both roles.
   */
  static hasSchedulingAssistantRole(roleNames: string[] | null | undefined): boolean {
    const normalizedNames = (roleNames ?? []).map(name => name.toLowerCase().replace(/[_\s]/g, ''));
    return normalizedNames.some(name =>
      name === 'schedulingassistant' ||
      name === 'scheduling_assistant' ||
      name === 'bookingadmin' ||
      name === 'booking_admin' ||
      (name.includes('scheduling') && name.includes('assistant')) ||
      (name.includes('booking') && name.includes('admin'))
    );
  }

  /** Whether a team member list row can appear in assignment pickers. */
  static isAssignableFromRoleNames(roleNames: string[] | null | undefined): boolean {
    const names = roleNames ?? [];
    if (names.length === 0) return false;
    const roleType = UserRoleConfigManager.determineRoleType(names);
    return UserRoleConfigManager.canBeAssignedToEvents(roleType);
  }

  /**
   * Check if a role name is a protected system role
   */
  static isSystemRole(roleName: string): boolean {
    const normalizedName = roleName.toLowerCase().replace(/\s+/g, ' ').trim();
    return SYSTEM_ROLE_NAMES.some(
      systemRole => systemRole.toLowerCase() === normalizedName
    );
  }
  
  /**
   * Check if user should be included in assignable users list
   * Based on role type and user-specific isAssignable flag
   */
  static isUserAssignable(roleType: UserRoleType, userIsAssignable?: boolean): boolean {
    const role = USER_ROLE_DEFINITIONS[roleType];
    
    // If role cannot be assigned to events at all, return false
    if (!role.canBeAssignedToEvents) {
      return false;
    }
    
    // If user has explicit setting, use that
    if (userIsAssignable !== undefined) {
      return userIsAssignable;
    }
    
    // Otherwise use the role's default
    return role.isAssignableByDefault;
  }
  
  /**
   * Get role features summary for display
   */
  static getRoleFeaturesSummary(roleType: UserRoleType): string[] {
    const role = USER_ROLE_DEFINITIONS[roleType];
    const features: string[] = [];
    
    if (role.canManageAllBookings) {
      features.push('Manage all bookings');
    } else {
      features.push('View own bookings only');
    }
    
    if (role.canManageRequests) {
      features.push('Manage booking requests');
    }
    
    if (role.canReassignUsers) {
      features.push('Reassign team members');
    }
    
    if (role.canReschedule) {
      features.push('Reschedule appointments');
    }
    
    if (role.canCancel) {
      features.push('Cancel appointments');
    }
    
    if (role.canEditBookingForms) {
      features.push('Edit booking forms');
    }
    
    if (role.canCreateBookingForms) {
      features.push('Create booking forms');
    }
    
    if (role.canBeAssignedToEvents) {
      features.push('Can be assigned to appointments');
    } else {
      features.push('Cannot be assigned to appointments');
    }
    
    if (role.canEditCompanySettings) {
      features.push('Edit company settings');
    } else if (role.hasCompanySettingsToggle) {
      features.push('Company settings (configurable)');
    }
    
    return features;
  }
}

// Export singleton for convenience
export const userRoleConfigManager = new UserRoleConfigManager();
